import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spin, Alert, Icon, Checkbox } from "antd";
import { moduleTourVideos } from "../../constants/moduleVideos";
import DEATH_API from "../../apis/death.api";
import "./ModalVideo.css";

const ModalVideo = ({
  module,
  thumbnailUrl = "https://via.placeholder.com/600x400?text=Video+Thumbnail",
  captionsUrl,
  autoOpen = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);

  const getUserRole = async () => {
    try {
      setLoading(true);
      const data = await DEATH_API.getUpdatedRole();
      if (data && data.data && data.data.setting) {
        localStorage.setItem("setting", JSON.stringify(data.data.setting));
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateModuleVideoSetting = async (moduleName, value) => {
    try {
      const settingRaw = localStorage.getItem("setting");
      let userSettings =
        settingRaw && settingRaw !== "undefined" ? JSON.parse(settingRaw) : {};

      // Ensure moduleVideos exists
      if (!userSettings.moduleVideos) {
        userSettings.moduleVideos = {};
      }

      // Update module value
      if (value) {
        userSettings.moduleVideos[moduleName] = true;
      }
      // else {
      //   delete userSettings.moduleVideos[moduleName];
      // }

      // Save locally
      localStorage.setItem("setting", JSON.stringify(userSettings));

      // Prepare payload for API
      const payload = {
        ...userSettings,
      };

      // Update setting in backend
      const response = await DEATH_API.updateUserAssetSetting(payload);

      if (response.status === 200) {
        console.log("Video setting updated successfully!");
        // Re-fetch latest settings to stay in sync
        await getUserRole();
      } else {
        console.log("Failed to update video setting.");
      }
    } catch (error) {
      console.log("Error updating video setting:", error);
      // message.error("Something went wrong while saving your preference.");
    }
  };

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10; // 10 * 300ms = 3 seconds
    const interval = setInterval(() => {
      const settingRaw = localStorage.getItem("setting");

      // Wait until setting is available
      if (settingRaw && settingRaw !== "undefined") {
        clearInterval(interval);

        let userSettings = JSON.parse(settingRaw);

        // Ensure moduleVideos exists in setting
        if (!userSettings.moduleVideos) {
          userSettings.moduleVideos = {};
          localStorage.setItem("setting", JSON.stringify(userSettings));
        }

        // Check if this module is hidden
        const hide = userSettings.moduleVideos[module];
        if (!hide && autoOpen) {
          setIsModalOpen(true);
        }
      } else if (attempts >= maxAttempts) {
        // Stop polling after timeout
        clearInterval(interval);
        console.warn("No setting found in localStorage after login check.");
      }

      attempts++;
    }, 300);

    return () => clearInterval(interval);
  }, [autoOpen, module]);

  const moduleVideo =
    moduleTourVideos && moduleTourVideos.find((vid) => vid.name === module);

  const handleClose = async () => {
    setIsModalOpen(false);

    const settingRaw = localStorage.getItem("setting");
    let userSettings =
      settingRaw && settingRaw !== "undefined" ? JSON.parse(settingRaw) : {};

    if (!userSettings.moduleVideos) {
      userSettings.moduleVideos = {};
    }

    if (dontShowAgain === "current") {
      // hide only current module
      userSettings.moduleVideos[module] = true;
    } else if (dontShowAgain === "all") {
      // hide all modules in one go
      const allModules = [
        "Financial Advisor",
        "Link Bank",
        "Tax & Inflation",
        "Source of Income",
        "Social Security",
        "Reports",
        "Rates of Return",
        "Protector",
        "Planner",
        "Organizer",
        "Manage Secure Links",
        "Liabilities & Credit",
        "Insurance",
        "Important Documents",
        "Client Contacts",
        "Goals",
        "Feedback",
        "FAQ Help & Support",
        "Divorce",
        "Disaster",
        "Disability",
        "Debt Payoff",
        "Death",
        "Dashboard",
        "Chat",
        "Budget",
        "Assets",
      ];

      allModules.forEach((mod) => {
        userSettings.moduleVideos[mod] = true;
      });
    }

    // Save locally
    localStorage.setItem("setting", JSON.stringify(userSettings));

    try {
      // single API call with updated userSettings
      const response = await DEATH_API.updateUserAssetSetting(userSettings);
      if (response && response.status === 200) {
        await getUserRole();
        if (dontShowAgain === "all") {
          console.log("All module videos have been hidden successfully.");
        } else if (dontShowAgain === "current") {
          console.log(`Video for "${module}" will not show again.`);
        }
      } else {
        console.log("Failed to update video setting.");
      }
    } catch (error) {
      console.log("Error updating settings:", error);
      console.log("Something went wrong while saving your preference.");
    }
  };

  if (!moduleVideo) {
    return (
      <div>
        <Alert
          message="Video Not Found"
          description={`No tutorial video found for module: "${module}". Please check the module name.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>
    );
  }

  return (
    <Modal
      visible={isModalOpen}
      footer={null}
      closable={false}
      width={700}
      onCancel={handleClose}
      destroyOnClose={true}
      className="video-modal-content"
      centered
    >
      <div>
        <div className="video-modal-header">
          <h3 className="tour-video-header">{moduleVideo.description}</h3>
          <Icon
            style={{ cursor: "pointer", fontSize: "16px" }}
            type="close"
            onClick={handleClose}
          />
        </div>

        {/* Loading overlay */}
        <div style={{ position: "relative" }}>
          {isVideoLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <img
                src={moduleVideo.thumbnailUrl || thumbnailUrl}
                alt="Video thumbnail"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 2,
                  borderRadius: "50%",
                  padding: "20px",
                }}
              >
                <Spin tip="Loading video…" />
              </div>
            </div>
          )}

          <video
            ref={videoRef}
            controls
            className="modal-video"
            src={moduleVideo.videoUrl}
            poster={moduleVideo.thumbnailUrl || thumbnailUrl}
            preload="metadata"
            onLoadStart={() => setIsVideoLoading(true)}
            onCanPlay={() => setIsVideoLoading(false)}
            onError={() => setIsVideoLoading(false)}
          >
            {captionsUrl && (
              <track kind="captions" src={captionsUrl} label="English" />
            )}
            Your browser does not support the video tag.
          </video>
        </div>

        <div
          className="video-controls"
          style={{
            marginTop: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "4px",alignItems:"baseline" }}>
            <Checkbox
              checked={dontShowAgain === "current"}
              onChange={(e) =>
                setDontShowAgain(e.target.checked ? "current" : null)
              }
            >
              Don’t show this again
            </Checkbox>

            <Checkbox
              checked={dontShowAgain === "all"}
              onChange={(e) =>
                setDontShowAgain(e.target.checked ? "all" : null)
              }
            >
              Hide all videos
            </Checkbox>
          </div>

          <button className="show-video-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalVideo;
